import { motion } from 'framer-motion';
import { ShieldCheck, Leaf, Users } from 'lucide-react';

const SustainabilityGovernance = () => {
  return (
    <section id="sustainability & governance" className="bg-white py-20 px-4 md:px-20">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
          Sustainability & Governance
        </h2>
        <p className="text-lg md:text-xl text-gray-600 mb-12">
          Built on Principles. Committed to the Future.
        </p>
        <p className="text-md md:text-lg text-gray-700 mb-16 max-w-4xl mx-auto">
          At <strong>Khalanga Global Ventures</strong>, we believe that real growth is only possible when it’s responsible. Sustainability and good governance are more than corporate checkboxes — they are at the core of how we build, operate, and impact the world around us. Our approach combines ethical leadership, social equity, and environmental foresight to create infrastructure that stands the test of time — both physically and morally.
        </p>

        <div className="grid md:grid-cols-3 gap-12 text-left">
          <motion.div 
            
            className="p-6 bg-gray-50 rounded-2xl shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Leaf className="h-10 w-10 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-black">Environmental Stewardship</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Green-certified and recycled materials</li>
              <li>Energy-efficient, low-emission project designs</li>
              <li>Robust waste management and recycling systems</li>
              <li>Water reuse and conservation systems</li>
              <li>Mandatory Environmental Impact Assessments</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Choosing Khalanga means reducing environmental liabilities and ensuring sustainable project delivery.
            </p>
          </motion.div>

          <motion.div 
            className="p-6 bg-gray-50 rounded-2xl shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Users className="h-10 w-10 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-black">Social Responsibility</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Empowerment through local hiring and training</li>
              <li>Skills development for youth and women</li>
              <li>Comprehensive health & safety practices</li>
              <li>Transparent community engagement models</li>
              <li>Infrastructure investments in project regions</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Clients benefit from ethical delivery partners that strengthen communities and drive inclusive growth.
            </p>
          </motion.div>

          <motion.div 
            className="p-6 bg-gray-50 rounded-2xl shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ShieldCheck className="h-10 w-10 text-black mb-4" />
            <h3 className="text-xl font-semibold mb-2 text-black">Governance & Compliance</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Independent board oversight</li>
              <li>Strict anti-corruption and ethics enforcement</li>
              <li>Transparent reporting frameworks</li>
              <li>Digitized compliance and contract tracking</li>
              <li>Ongoing internal ethics training</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              Trust Khalanga’s systems to maintain full legal, financial, and reputational compliance.
            </p>
          </motion.div>
        </div>

        <motion.div 
          className="mt-20 bg-blue-50 p-8 rounded-2xl max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <blockquote className="text-lg italic text-gray-800">
            “At Khalanga Global Ventures, we don’t just build for today — we build for the generations to come. Our work is driven by a responsibility to the planet, the people, and the principles that govern us all.”
          </blockquote>
          <p className="mt-4 text-sm text-gray-600 text-right">— Board of Directors</p>
        </motion.div>
      </div>
    </section>
  );
};

export default SustainabilityGovernance;